import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ListFilter, Search, X } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link, type To } from 'react-router-dom';

import { RefreshButton } from './refresh-button';
import { useAppBar, type IAppBar } from './zone';

/**
 * A work page's compact bar (SR-485): the page scrolls as any page does, and once its header, cards
 * and filters have gone under the app bar, the app bar's row draws them slimmed — the title, the
 * tabs, the cards as chips, the actions and the filter's controls as icons, its chips on one line —
 * with the table's column header sticking under it. Back near the top the bar fades out over the
 * page's own header, which never left the flow; nothing scroll-driven changes the page's layout, so
 * a list too short to scroll never shows the bar, and nothing can oscillate.
 *
 * **The bar's controls are the page's, drawn condensed.** The page renders them a second time inside
 * `CompactBar`, which provides `CondensedScope`; the pieces that slim read it (`useCondensed`) —
 * the state cards, *Advanced…*, *Columns*, the filter chips, the header's
 * actions. The list's search stays a box (`CompactSearch`), and the page's quick filters and its
 * filter line stay in the bar. Outside the bar nothing is condensed. A slimmed
 * control keeps its accessible name — its words move into `aria-label` and a tooltip — and runs the
 * same handler as its twin in the page.
 *
 * **The bar is drawn only while it shows**, so a spec at the top of the page finds every control
 * once. Its focus is never lost: a control focused in the bar as it goes hands the focus to its twin
 * in the page, and the app bar's own controls, hidden under it, hand theirs to the bar's 🔍 — the
 * app's search, "Search everything", at the bar's end as in the app bar, apart from the list's box.
 *
 * **The app bar is the shell's** (`ZoneProvider`'s `appBar`): the element the bar is drawn over, the find
 * palette's name and opener — Ctrl/⌘+K opens it all the same — and the switch that hides the app
 * bar's own row under the bar. A page outside a shell that provides one draws no bar.
 */

export interface ICondensed {
  condensed: boolean;
}

const CondensedContext = React.createContext<ICondensed>({ condensed: false });

export const CondensedScope = CondensedContext.Provider;

export function useCondensed(): ICondensed {
  return React.useContext(CondensedContext);
}

/**
 * A work page's quick filters drawn by name alone (`QuickChipsScope`): each chip its name and a dot
 * while it narrows, never its value, so the toolbar's row never grows with what is chosen — the
 * values are the applied filters' row (`CompactChipLine`). `open` and `setOpen`, where given, hold
 * which chip's control is open, by its name, so a chip of that row opens it.
 */
export interface IQuickChips {
  open?: Nullable<string>;
  setOpen?: (label: Nullable<string>) => void;
}

const QuickChipsContext = React.createContext<Nullable<IQuickChips>>(null);

export const QuickChipsScope = QuickChipsContext.Provider;

export function useQuickChips(): Nullable<IQuickChips> {
  return React.useContext(QuickChipsContext);
}

/** Names alone, each control opened by its own press. */
export const NAMES_ONLY: IQuickChips = {};

/** The app bar's height — the row the bar takes, and where a sticky column header sits. */
export const APP_BAR_HEIGHT = 48;

/** Where a work page's sticky column header sits: under the compact bar as tall as it is drawn
 *  (`--compact-bar-height`, which the bar says on the document while it shows, a second row and
 *  all), else under the app bar; and under the selection bar while rows are ticked
 *  (`--selection-bar-height`, which the selection bar says on its parent). */
export const STICKY_TABLE_TOP = `calc(var(--compact-bar-height, ${APP_BAR_HEIGHT}px) + var(--selection-bar-height, 0px))`;

/** Where a work page's selection bar sticks: under the compact bar as tall as it is drawn, its
 *  applied filters' row and all, else under the app bar — so ticked rows never slide the bar under
 *  that row. */
export const STICKY_BAR_TOP = `var(--compact-bar-height, ${APP_BAR_HEIGHT}px)`;

/** Said on `window` as the bar's height changes — a header moved by hand follows it without a
 *  scroll (`DataTable`'s wide tables). */
export const COMPACT_BAR_RESIZE = 'compactbarresize';

/** How long the bar fades and slides in or out. */
const FADE_MS = 150;

/** How far below the window `useScrolledPast`'s root reaches: deeper than any page's controls. */
const BELOW_WINDOW = 100_000;

/**
 * Whether `sentinel` — an element the page draws where its own controls end — has scrolled up under
 * the app bar: an `IntersectionObserver`, no layout read on scroll. Below the window it is not past.
 *
 * The observer's root reaches far below the window (`BELOW_WINDOW`), so the app bar's lower edge is
 * the one line the sentinel crosses. Over the window alone, a jump from under the app bar to below
 * the window — Home, the scrollbar, a tall page's top on a short screen — never intersects, so no
 * callback says it and the bar stays drawn over the page's own controls (and the jump back never
 * draws it).
 */
export function useScrolledPast(sentinel: Nullable<HTMLElement>): boolean {
  const [past, setPast] = React.useState(false);
  React.useEffect(() => {
    setPast(false);
    const element = sentinel;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const top = entry.rootBounds?.top ?? APP_BAR_HEIGHT;
        setPast(!entry.isIntersecting && entry.boundingClientRect.bottom <= top);
      },
      { rootMargin: `-${APP_BAR_HEIGHT}px 0px ${BELOW_WINDOW}px 0px`, threshold: 0 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [sentinel]);
  return past;
}

/**
 * A work page's own controls — its toolbar and the filters applied under it — as one block, with the
 * sentinel `useScrolledPast` watches on its bottom edge. The sentinel takes no row of its own: a
 * zero-height row in the page's grid still gets the grid's gap, which left an empty 12 px under every
 * toolbar. What follows the block keeps the page's one gap.
 */
export function WorkControls({
  end,
  children
}: {
  /** The page's sentinel setter — the element `useScrolledPast` is handed. */
  end: (element: Nullable<HTMLElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative grid min-w-0 gap-3">
      {children}
      <div ref={end} aria-hidden data-controls-end="" className="pointer-events-none absolute bottom-0 h-0 w-full" />
    </div>
  );
}

/**
 * The bar drawn over the app bar's row while `shown` — faded in and out, still under reduced motion.
 * `actions` — the header's icons — and the app's search end it in a group that never shrinks: a
 * crowded page gives way inside `children`, which clips at its own edge, never past them. `below`
 * is a second row under the app bar's — the filters applied (`CompactChipLine`) — drawn only while
 * the page passes one; the bar says its height for the sticky column header (`STICKY_TABLE_TOP`).
 */
export function CompactBar({
  shown,
  label,
  actions,
  below,
  children
}: {
  shown: boolean;
  label: string;
  actions?: React.ReactNode;
  below?: React.ReactNode;
  children: React.ReactNode;
}) {
  const appBar = useAppBar();
  // `in` once drawn a frame, so the transition runs; `out` while it fades before it goes.
  const [phase, setPhase] = React.useState<'gone' | 'drawn' | 'in' | 'out'>('gone');
  React.useEffect(() => {
    if (shown) {
      setPhase((now) => (now === 'gone' ? 'drawn' : 'in'));
      return;
    }
    setPhase((now) => (now === 'gone' ? now : 'out'));
    const timer = window.setTimeout(() => setPhase('gone'), reducedMotion() ? 0 : FADE_MS);
    return () => window.clearTimeout(timer);
  }, [shown]);
  React.useEffect(() => {
    if (phase !== 'drawn') return;
    const frame = requestAnimationFrame(() => setPhase('in'));
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  const setCompact = appBar?.setCompact;
  const covering = phase === 'drawn' || phase === 'in';
  React.useEffect(() => {
    if (!setCompact || !covering) return;
    setCompact(true);
    return () => setCompact(false);
  }, [setCompact, covering]);

  if (!appBar?.host || phase === 'gone') return null;
  return createPortal(
    <Bar appBar={appBar} label={label} actions={actions} below={below} visible={phase === 'in'}>
      {children}
    </Bar>,
    appBar.host
  );
}

function Bar({
  appBar,
  label,
  actions,
  below,
  visible,
  children
}: {
  appBar: IAppBar;
  label: string;
  actions: React.ReactNode;
  below: React.ReactNode;
  visible: boolean;
  children: React.ReactNode;
}) {
  const bar = React.useRef<HTMLDivElement>(null);
  const find = React.useRef<HTMLButtonElement>(null);
  const lastFocus = React.useRef<Nullable<HTMLElement>>(null);
  React.useLayoutEffect(() => {
    // The app bar's own controls go under the bar: one focused hands the focus to the bar's 🔍.
    const active = document.activeElement;
    if (active instanceof HTMLElement && appBar.host?.contains(active) && !bar.current?.contains(active)) {
      find.current?.focus({ preventScroll: true });
    }
    const box = bar.current;
    return () => {
      // Going, the bar hands a focused control's focus to its twin in the page.
      const was = lastFocus.current;
      if (!box || !was || !box.contains(was)) return;
      requestAnimationFrame(() => {
        const now = document.activeElement;
        if (now && now !== document.body && now.isConnected) return;
        twinOf(was)?.focus({ preventScroll: true });
      });
    };
  }, [appBar.host]);
  // The bar's height, a second row and all, for the sticky column header: read as the bar's box
  // changes — a filter added or cleared — never on scroll; gone with the bar.
  React.useLayoutEffect(() => {
    const box = bar.current;
    if (!box) return;
    const root = document.documentElement;
    const say = (height: Nullable<number>) => {
      if (height === null) root.style.removeProperty('--compact-bar-height');
      else root.style.setProperty('--compact-bar-height', `${height}px`);
      window.dispatchEvent(new Event(COMPACT_BAR_RESIZE));
    };
    const measure = () => say(Math.max(APP_BAR_HEIGHT, box.offsetHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => {
      observer.disconnect();
      say(null);
    };
  }, []);
  return (
    <CondensedScope value={CONDENSED}>
      <div
        ref={bar}
        role="region"
        aria-label={label}
        data-compact-bar=""
        onFocus={(event) => {
          if (event.target instanceof HTMLElement) lastFocus.current = event.target;
        }}
        onBlur={(event) => {
          // Focus gone from the bar: there is nothing of it to hand back when it goes.
          if (!(event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))) {
            lastFocus.current = null;
          }
        }}
        className={cn(
          'absolute inset-x-0 top-0 flex min-h-full flex-col bg-white',
          below && 'border-b border-border shadow-[0_2px_4px_-2px_rgb(0_0_0/0.08)]',
          'transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
          visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'
        )}
      >
        <div className="flex min-h-12 flex-1 items-center gap-1.5 px-4">
          <div className="@container flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">{children}</div>
          <div className="flex shrink-0 items-center gap-1">
            {actions}
            {appBar.refresh && (
              <RefreshButton
                reading={appBar.refresh.reading}
                readAt={appBar.refresh.readAt}
                onClick={appBar.refresh.onClick}
              />
            )}
            <CondensedTip label={<FindTip findLabel={appBar.findLabel} />}>
              <Button
                ref={find}
                type="button"
                variant="ghost"
                size="icon"
                aria-label={SEARCH_EVERYTHING}
                aria-description={appBar.findLabel}
                aria-keyshortcuts="Control+K Meta+K"
                className="ml-1 h-8 w-10 shrink-0 rounded-md bg-[color-mix(in_srgb,var(--color-royal-blue)_6%,white)] text-muted-foreground hover:bg-[color-mix(in_srgb,var(--color-royal-blue)_9%,white)]"
                onClick={appBar.onFind}
              >
                <Search className="size-4" aria-hidden />
              </Button>
            </CondensedTip>
            {appBar.recent}
          </div>
        </div>
        {/* One height whatever the chips: a filter changes the bar's height, its words never. */}
        {below && <div className="flex h-10 items-center border-t border-border/70 px-4 [&>*]:min-w-0 [&>*]:flex-1">{below}</div>}
      </div>
    </CondensedScope>
  );
}

const CONDENSED: ICondensed = { condensed: true };

/** The app's search, as its triggers are named apart from a list's box. */
export const SEARCH_EVERYTHING = 'Search everything';

/** The app's search in a tooltip: its name, its shortcut, and what this account's search reaches. */
export function FindTip({ findLabel }: { findLabel: string }) {
  return (
    <span className="grid gap-0.5">
      <span>
        {SEARCH_EVERYTHING} <kbd className="font-sans opacity-80">Ctrl K</kbd>
      </span>
      <span className="opacity-80">{findLabel}</span>
    </span>
  );
}

/** Controls a part of the page draws into the bar — `to`, a slot the bar holds — condensed as the
 *  bar's own are; nothing while the bar is not drawn. */
export function CompactPortal({ to, children }: { to: Nullable<HTMLElement>; children: React.ReactNode }) {
  return to ? createPortal(<CondensedScope value={CONDENSED}>{children}</CondensedScope>, to) : null;
}

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The element in the page standing for `was`: the same tag, going to the same place or of the same
 *  name. */
function twinOf(was: HTMLElement): Nullable<HTMLElement> {
  const href = was.getAttribute('href');
  const name = was.getAttribute('aria-label') ?? was.textContent?.trim();
  const same = (candidate: HTMLElement) =>
    href !== null
      ? candidate.getAttribute('href') === href
      : (candidate.getAttribute('aria-label') ?? candidate.textContent?.trim()) === name;
  return [...document.querySelectorAll<HTMLElement>(was.tagName)].find(same) ?? null;
}

/** The page's title, as the bar draws it first, always whole: a crowded bar narrows its search. */
export function CompactTitle({ children }: { children: React.ReactNode }) {
  return <span className="shrink-0 text-[0.9375rem] font-medium text-[var(--color-royal-blue)]">{children}</span>;
}

/** An entry of `CompactMenu`: where it goes, and whether it is the one open. */
export interface ICompactMenuItem {
  key: string;
  label: React.ReactNode;
  to: To;
  current: boolean;
  replace?: boolean;
  /** Said after the label, quiet — a tab's count. */
  count?: React.ReactNode;
}

/**
 * A row of tabs in the bar — the surveys, the queues, a page's lists past what fits — as the open
 * one's name and a ▾ opening the rest as a menu of links, each going where its tab goes.
 */
export function CompactMenu({
  label,
  trigger,
  items,
  chip = false
}: {
  /** The menu's name, the open entry's words after it. */
  label: string;
  /** The trigger's words — the open entry's, or the menu's name where the open entry is drawn beside it. */
  trigger: React.ReactNode;
  items: readonly ICompactMenuItem[];
  /** The trigger drawn as the open card — a page's cards as one chip with its ▾. */
  chip?: boolean;
}) {
  const open = items.find((item) => item.current);
  const said = typeof open?.label === 'string' ? open.label : typeof trigger === 'string' ? trigger : '';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 shrink-0 gap-1 px-2 font-medium',
            chip &&
              'gap-1.5 border border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] px-2.5 shadow-[inset_0_-3px_0_var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
          )}
          aria-label={said ? `${label}: ${said}` : label}
        >
          {trigger}
          <ChevronDown className="size-3.5 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[70dvh] overflow-y-auto">
        {items.map((item) => (
          <DropdownMenuItem key={item.key} asChild>
            <Link
              to={item.to}
              replace={item.replace}
              aria-current={item.current ? 'page' : undefined}
              className={cn('flex items-baseline gap-3', item.current && 'font-medium')}
            >
              <span className="flex-1">{item.label}</span>
              {item.count !== undefined && (
                <span className="text-xs text-muted-foreground tabular-nums">{item.count}</span>
              )}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A list's search in the bar: the page's box, narrower (`FilterBox`), never folded to an icon — an
 * icon there was taken for the app's search beside it. The draft and the submit are the page's
 * box's own; Enter sends it.
 */
export function CompactSearch({
  label,
  placeholder,
  draft,
  onDraft,
  onSubmit
}: {
  label: string;
  placeholder: string;
  draft: string;
  onDraft: (draft: string) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      role="search"
      className="w-56 min-w-24 shrink-[50]"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FilterBox
        label={label}
        placeholder={placeholder}
        value={draft}
        onChange={onDraft}
        className="block"
        inputClassName="h-8"
      />
    </form>
  );
}

/**
 * A work page's search box. It filters the list it heads, so it says so — the filter icon and
 * "Filter people…" — and is never drawn as the 🔍, which is the app's own search ("Search
 * everything", in the app bar). The page and its compact bar draw it over one draft; the bar's is
 * narrower, never folded to an icon.
 */
export function FilterBox({
  label,
  placeholder,
  value,
  onChange,
  className,
  inputClassName
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
}) {
  return (
    <span className={cn('relative min-w-0', className)}>
      <ListFilter
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn('pl-9', inputClassName)}
      />
    </span>
  );
}

/** The line between two of the bar's zones — where I am, narrowing the list, the view, the app. */
export function CompactDivider() {
  return <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />;
}

/**
 * The page's quick filters behind one button in the bar — "Filters" and how many narrow the list —
 * opening a popover of the same controls as the page's toolbar, drawn as there (not condensed).
 * Open and shut by the page where a chip of the applied filters opens it too.
 */
export function CompactFilters({
  count,
  open,
  onOpenChange,
  children
}: {
  /** The quick filters set. */
  count: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2.5"
          aria-label={count > 0 ? `Quick filters, ${count} active` : 'Quick filters'}
        >
          Filters
          {count > 0 && (
            <span className="rounded-full bg-[var(--color-royal-blue)] px-1.5 text-[0.6875rem] leading-4 font-semibold text-white tabular-nums">
              {count}
            </span>
          )}
          <ChevronDown className="size-3.5 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Quick filters"
        className="flex w-auto max-w-[min(40rem,90vw)] flex-wrap items-center gap-2 p-3"
      >
        <QuickFilters>{children}</QuickFilters>
      </PopoverContent>
    </Popover>
  );
}

/** A page's quick filters inside a menu — *Filters (n)*, the bar's or the toolbar's — drawn as in
 *  the toolbar (not condensed), by name, under the page's own scope where it holds which is open. */
export function QuickFilters({ children }: { children: React.ReactNode }) {
  const quick = useQuickChips() ?? NAMES_ONLY;
  return (
    <CondensedScope value={EXPANDED}>
      <QuickChipsScope value={quick}>{children}</QuickChipsScope>
    </CondensedScope>
  );
}

const EXPANDED: ICondensed = { condensed: false };

/** One chip of `CompactChipLine` — its key, and the chip itself (its press and its ✕ its own). */
export interface ICompactChip {
  key: string;
  chip: React.ReactNode;
}

/** The room the "+N" button takes, with its gap. */
const MORE_ROOM = 64;
const CHIP_GAP = 6;

/**
 * The bar's second row: the filters applied, as chips, and *Clear all* right after the last — one
 * line, never wrapped; the chips past the room fold into "+N ▾", which opens the rest. What fits is
 * measured on a hidden copy of the whole line, so what is drawn never changes what fits.
 */
export function CompactChipLine({
  chips,
  onClearAll,
  label = 'Active filters'
}: {
  chips: readonly ICompactChip[];
  onClearAll: () => void;
  /** The group's name — "Filters" in a page, where the suites find the line by it. */
  label?: string;
}) {
  const room = React.useRef<HTMLDivElement>(null);
  const whole = React.useRef<HTMLDivElement>(null);
  const clear = React.useRef<HTMLButtonElement>(null);
  const [fit, setFit] = React.useState(chips.length);
  const keys = chips.map((chip) => chip.key).join('\n');
  React.useLayoutEffect(() => {
    const box = room.current;
    const copy = whole.current;
    if (!box || !copy) return;
    const measure = () => {
      const widths = [...copy.children].map((child) => (child as HTMLElement).offsetWidth + CHIP_GAP);
      const all = widths.reduce((sum, width) => sum + width, 0);
      // *Clear all* follows the chips in the same row, so its width is not theirs.
      const roomWidth = box.clientWidth - (clear.current?.offsetWidth ?? 0) - CHIP_GAP;
      if (all <= roomWidth + CHIP_GAP) {
        setFit(widths.length);
        return;
      }
      let left = roomWidth - MORE_ROOM;
      let count = 0;
      while (count < widths.length && left >= widths[count]) left -= widths[count++];
      setFit(count);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(copy);
    return () => observer.disconnect();
  }, [keys]);
  const rest = chips.slice(fit);
  return (
    <div role="group" aria-label={label} className="flex min-w-0 items-center gap-2">
      <div ref={room} className="relative flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        <div
          ref={whole}
          aria-hidden
          data-measure-copy=""
          className="pointer-events-none invisible absolute top-0 left-0 flex w-max items-center gap-1.5"
        >
          {chips.map((chip) => (
            <span key={chip.key} className="flex shrink-0">
              {chip.chip}
            </span>
          ))}
        </div>
        {chips.slice(0, fit).map((chip) => (
          <span key={chip.key} className="flex min-w-0 max-w-full shrink-0">
            {chip.chip}
          </span>
        ))}
        {rest.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1 rounded-full px-2.5 text-[0.8125rem]"
                aria-label={`${rest.length} more ${rest.length === 1 ? 'filter' : 'filters'}`}
              >
                +{rest.length}
                <ChevronDown className="size-3.5 opacity-60" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              aria-label="More filters"
              className="flex w-auto max-w-[min(36rem,90vw)] flex-wrap gap-1.5 p-2"
            >
              {rest.map((chip) => (
                <span key={chip.key} className="flex min-w-0 max-w-full">
                  {chip.chip}
                </span>
              ))}
            </PopoverContent>
          </Popover>
        )}
        <Button
          ref={clear}
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 gap-1 px-2"
          onClick={onClearAll}
        >
          <X className="size-3.5" aria-hidden />
          Clear all
        </Button>
      </div>
    </div>
  );
}

/**
 * A control's words as a tooltip while it is condensed to its icon — the same element either way, so
 * the focus stays on it; expanded, the tooltip never opens.
 */
export function CondensedTip({
  label,
  always = false,
  children
}: {
  label: React.ReactNode;
  /** Said at full width too — why a control is off, which its words alone do not say. */
  always?: boolean;
  children: React.ReactElement;
}) {
  const { condensed } = useCondensed();
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip open={(condensed || always) && open} onOpenChange={setOpen}>
      {/* A span of its own: the trigger's `data-slot` on the button would drop the button's styles. */}
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Scrolls the window so the table above `pager` starts right under the sticky bars — the app bar or
 * the compact bar standing in for it (`--compact-bar-height`), and a selection bar
 * (`--selection-bar-height`, set on the list's own box, so read off the table) — where its top is
 * scrolled away, and within the table's own scrolling frame first; a table in view is left where it
 * is. The table is the last one above the pager in the nearest box holding one, never past the
 * pager's own section, dialog or popover: a list there with no table drawn moves nothing behind it.
 */
export function toListTop(pager: Nullable<HTMLElement>): void {
  if (pager === null) return;
  let table: Nullable<Element> = null;
  for (let box = pager.parentElement; box !== null && table === null; box = box.parentElement) {
    const above = [...box.querySelectorAll('table')].filter(
      (each) => each.compareDocumentPosition(pager) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    table = above.at(-1) ?? null;
    if (box.matches(LIST_BOUNDS)) break;
  }
  if (table === null) return;
  // A table that scrolls in its own frame (Lists) is brought to that frame's top first.
  const frame = scrollingFrame(table);
  if (frame !== null) {
    const inFrame = table.getBoundingClientRect().top - frame.getBoundingClientRect().top;
    if (inFrame < 0) frame.scrollBy({ top: inFrame });
  }
  const bars =
    (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--compact-bar-height')) ||
      APP_BAR_HEIGHT) + (parseFloat(getComputedStyle(table).getPropertyValue('--selection-bar-height')) || 0);
  const top = table.getBoundingClientRect().top - bars;
  if (top < 0) window.scrollBy({ top });
}

/** The nearest box around `table` that scrolls it vertically, short of the document. */
function scrollingFrame(table: Element): Nullable<HTMLElement> {
  for (let box = table.parentElement; box !== null && box !== document.body; box = box.parentElement) {
    const overflow = getComputedStyle(box).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && box.scrollHeight > box.clientHeight) return box;
  }
  return null;
}

/** The boxes a list's table is never looked for past. */
const LIST_BOUNDS = 'section, [role="dialog"], [data-radix-popper-content-wrapper], main';
